// #1230 Web Push 購読クライアント。
//
// iOS の制約: Web Push は「ホーム画面に追加した PWA (standalone)」でのみ動く。
// Safari のブラウザ表示では Notification API 自体が存在しないため、
// needsA2hsForPush() が true の端末には購読の代わりに A2HS レクチャーを出す
// (components/NotificationPermissionPrompt.tsx)。
import { api } from '../services/api'

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  )
}

export function isIos(): boolean {
  // iPadOS 13+ は Mac を名乗るため maxTouchPoints で判別する
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  )
}

/** iOS で Push を受けるにはホーム画面追加 (standalone 起動) が必須 */
export function needsA2hsForPush(): boolean {
  return isIos() && !isStandalone()
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(b64)
  // ArrayBuffer を明示して確保する — TS 5.9 の BufferSource は
  // Uint8Array<ArrayBufferLike> (SharedArrayBuffer の可能性) を受け付けない
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/**
 * 通知許可済みの端末で Push 購読を確立し、サーバに登録する。
 *
 * - 未対応ブラウザ / 未許可 / VAPID 未設定 (local 既定) / SW 未登録 (vite dev) では
 *   静かに false を返す — 通知は補助機能であり、本体のフローを妨げない
 * - 冪等: 既存購読があれば再利用してサーバへ upsert するだけなので、App 起動ごとに
 *   呼んでも購読が増殖しない (doc id = endpoint hash)
 */
/** subscribeToPush の結果。失敗を1つにまとめると、利用者に出す説明が書けない。 */
export type PushSubscribeResult = 'ok' | 'denied' | 'unsupported' | 'failed'

/**
 * **利用者の操作を起点に**購読を確立する。未許可ならその場で許可を求める。
 *
 * ensurePushSubscription() が許可を求めないのは、画面を開いただけで通知ダイアログを
 * 出さないため（起動時にも呼ばれる）。その結果、設定トグルから呼ぶと**許可を一度も
 * 聞かないまま静かに失敗し、押しても何も起きないスイッチ**になっていた。
 * 操作起点の入口をこちらに分けて、失敗した理由を呼び出し側が説明できるようにする。
 */
export async function subscribeToPush(locale: string): Promise<PushSubscribeResult> {
  if (!pushSupported() || needsA2hsForPush()) return 'unsupported'
  if (Notification.permission === 'default') {
    // ブラウザによっては拒否のまま解決するので、戻り値で判定する
    if ((await Notification.requestPermission()) !== 'granted') return 'denied'
  } else if (Notification.permission !== 'granted') {
    // 一度拒否された後はダイアログが出ない。ブラウザ設定から戻すしかない
    return 'denied'
  }
  return (await ensurePushSubscription(locale)) ? 'ok' : 'failed'
}

export async function ensurePushSubscription(locale: string): Promise<boolean> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return false
    const { publicKey } = await api.getPushVapidKey()
    if (!publicKey) return false
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return false
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
    }
    await api.registerPushSubscription(sub.toJSON(), locale)
    return true
  } catch {
    return false
  }
}
